<template>
    <div class="quickpay-deadlines" :class="rootClasses">
        <div class="quickpay-deadlines__header">
            <span class="quickpay-deadlines__title typographies-paragraph-medium-semibold">
                {{ computedTitle }}
            </span>
        </div>
        <div class="quickpay-deadlines__preview">
            <div
                v-for="deadline in firstTwoDeadlines"
                :key="deadline.id"
                class="quickpay-deadlines__item"
                :class="getItemClasses(deadline)"
                @click="handleDeadlineClick(deadline)"
            >
                <div class="quickpay-deadlines__item-main">
                    <CompassRadio
                        v-if="selectable"
                        :modelValue="deadline.id"
                        name="deadline-selection"
                        :showLabel="false"
                        :checked="selectedDeadlineId === deadline.id"
                        :disabled="deadline.step === 'paid'"
                        class="quickpay-deadlines__item-radio"
                        @change="() => handleDeadlineClick(deadline)"
                        @click.stop
                    />
                    <span
                        class="quickpay-deadlines__item-date typographies-paragraph-medium-regular"
                        :class="getDateClasses(deadline)"
                    >
                        {{ formatDate(deadline.date) }}
                    </span>
                    <CompassPill
                        v-if="getDeadlineStatus(deadline)"
                        v-bind="getDeadlineStatus(deadline)"
                        size="small"
                        class="quickpay-deadlines__item-tag"
                    />
                </div>
                <span
                    class="quickpay-deadlines__item-amount typographies-paragraph-medium-bold"
                    :class="getAmountClasses(deadline)"
                >
                    {{ formatAmount(deadline.amount_total) }}
                </span>
            </div>
        </div>
        <div class="quickpay-deadlines__wrapper">
            <button
                v-if="hasMoreDeadlines"
                class="quickpay-deadlines__toggle"
                type="button"
                @click="toggleDeadlines"
            >
                <span class="quickpay-deadlines__toggle-text typographies-paragraph-medium-regular">
                    {{ translations.viewAll }}
                </span>
                <CompassIcon
                    v-if="!isExpanded"
                    icon="caret-right-fill"
                    size="small"
                    class="quickpay-deadlines__toggle-icon"
                />
                <CompassIcon
                    v-else
                    icon="caret-down-fill"
                    size="small"
                    class="quickpay-deadlines__toggle-icon"
                />
            </button>
            <transition-group name="deadline-item" tag="div" class="quickpay-deadlines__list">
                <div
                    v-for="(deadline, index) in displayedRemainingDeadlines"
                    :key="deadline.id"
                    :style="{ '--item-index': index, '--total-items': remainingDeadlines.length }"
                    class="quickpay-deadlines__item"
                    :class="getItemClasses(deadline)"
                    @click="handleDeadlineClick(deadline)"
                >
                    <div class="quickpay-deadlines__item-main">
                        <CompassRadio
                            v-if="selectable"
                            :modelValue="deadline.id"
                            name="deadline-selection"
                            :showLabel="false"
                            :checked="selectedDeadlineId === deadline.id"
                            :disabled="deadline.step === 'paid'"
                            class="quickpay-deadlines__item-radio"
                            @change="() => handleDeadlineClick(deadline)"
                            @click.stop
                        />
                        <span
                            class="quickpay-deadlines__item-date typographies-paragraph-medium-regular"
                            :class="getDateClasses(deadline)"
                        >
                            {{ formatDate(deadline.date) }}
                        </span>
                        <CompassPill
                            v-if="getDeadlineStatus(deadline)"
                            v-bind="getDeadlineStatus(deadline)"
                            size="small"
                            class="quickpay-deadlines__item-tag"
                        />
                    </div>
                    <span
                        class="quickpay-deadlines__item-amount typographies-paragraph-medium-bold"
                        :class="getAmountClasses(deadline)"
                    >
                        {{ formatAmount(deadline.amount_total) }}
                    </span>
                </div>
            </transition-group>
        </div>
        <transition name="confirmation-block">
            <div
                v-if="selectable && selectedDeadline"
                class="quickpay-deadlines__confirmation"
                :class="confirmationClasses"
            >
                <div class="quickpay-deadlines__confirmation-content">
                    <div class="quickpay-deadlines__confirmation-info">
                        <span
                            class="quickpay-deadlines__confirmation-amount typographies-paragraph-large-bold"
                        >
                            {{ formatAmount(selectedDeadline.amount_due) }}
                        </span>
                        <span
                            class="quickpay-deadlines__confirmation-date typographies-paragraph-small-regular"
                        >
                            {{ translations.deadlineOf }} {{ formatDate(selectedDeadline.date) }}
                        </span>
                    </div>
                    <CompassButton
                        type="primary-contrast"
                        :label="translations.paySelection"
                        @click="handlePayClick"
                    />
                </div>
            </div>
        </transition>
        <template v-if="selectable">
            <div class="quickpay-deadlines__divider typographies-paragraph-medium-semibold">
                {{ translations.orDivider }}
            </div>

            <div class="quickpay-deadlines__pay-whole">
                <CompassButton
                    type="tertiary"
                    :label="translations.payWholeDocument"
                    @click="handlePayWholeDocument"
                />
            </div>
        </template>
    </div>
</template>

<script>
export default {
    props: {
        deadlines: {
            type: Array,
            required: true,
        },
        currency: {
            type: String,
            required: true,
        },
        hasFooter: {
            type: Boolean,
            default: false,
        },
        selectable: {
            type: Boolean,
            default: false,
        },
        preselectedDeadlineId: {
            type: Number,
            default: null,
        },
        autoExpand: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['pay-deadline', 'pay-whole-document'],
    data() {
        return {
            isExpanded: false,
            selectedDeadlineId: null,
            translations: {
                title: this.$t.__('quickpay.QuickpayDeadlines.title'),
                selectTitle: this.$t.__('quickpay.QuickpayDeadlines.selectTitle'),
                late: this.$t.__('common.labels.isLate'),
                paid: this.$t.__('common.labels.paid'),
                viewAll: this.$t.__('quickpay.QuickpayDeadlines.viewAll'),
                paySelection: this.$t.__('quickpay.QuickpayDeadlines.paySelection'),
                payWholeDocument: this.$t.__('quickpay.QuickpayDeadlines.payWholeDocument'),
                deadlineOf: this.$t.__('quickpay.QuickpayDeadlines.deadlineOf'),
                orDivider: this.$t.__('common.labels.or').toUpperCase(),
            },
        };
    },
    computed: {
        userLocale() {
            const locale = window.App?.locale || 'fr_FR';
            return locale.split('.')[0].replace('_', '-');
        },
        computedTitle() {
            return this.selectable ? this.translations.selectTitle : this.translations.title;
        },
        sortedDeadlines() {
            return [...this.deadlines].sort((a, b) => {
                const aIsLate = this.isLate(a.date) && a.step !== 'paid';
                const bIsLate = this.isLate(b.date) && b.step !== 'paid';
                const aIsPaid = a.step === 'paid';
                const bIsPaid = b.step === 'paid';
                // Late deadlines first, then paid deadlines, then by date
                if (aIsLate && !bIsLate) return -1;
                if (!aIsLate && bIsLate) return 1;
                if (aIsPaid && !bIsPaid) return 1;
                if (!aIsPaid && bIsPaid) return -1;
                return a.date - b.date;
            });
        },
        firstTwoDeadlines() {
            return this.sortedDeadlines.slice(0, 2);
        },
        remainingDeadlines() {
            return this.sortedDeadlines.slice(2);
        },
        hasMoreDeadlines() {
            return this.sortedDeadlines.length > 2;
        },
        displayedRemainingDeadlines() {
            return this.isExpanded ? this.remainingDeadlines : [];
        },
        selectedDeadline() {
            return this.sortedDeadlines.find((deadline) => deadline.id === this.selectedDeadlineId);
        },
        rootClasses() {
            return {
                'quickpay-deadlines--selectable': this.selectable,
                'quickpay-deadlines--with-confirmation': this.selectable && this.selectedDeadline,
                'quickpay-deadlines--with-footer': this.hasFooter,
            };
        },
        confirmationClasses() {
            return {
                'quickpay-deadlines__confirmation--with-footer': this.hasFooter,
            };
        },
    },
    mounted() {
        if (this.selectable) {
            this.selectedDeadlineId = this.preselectedDeadlineId;
            this.isExpanded = this.autoExpand;
        }
    },
    methods: {
        toggleDeadlines() {
            this.isExpanded = !this.isExpanded;
        },
        formatDate(timestamp) {
            const date = new Date(timestamp * 1000);
            return new Intl.DateTimeFormat(this.userLocale, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
            }).format(date);
        },
        formatAmount(amount) {
            const numericAmount = parseFloat(amount);
            return new Intl.NumberFormat(this.userLocale, {
                style: 'currency',
                currency: this.currency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(numericAmount);
        },
        isLate(timestamp) {
            const deadlineDate = new Date(timestamp * 1000);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return deadlineDate < today;
        },
        getDeadlineStatus(deadline) {
            switch (deadline.step) {
                case 'paid':
                    return {
                        label: this.translations.paid,
                        color: 'green',
                        icon: 'check',
                    };
                case 'due':
                case 'payinprogress':
                    if (this.isLate(deadline.date)) {
                        return {
                            label: this.translations.late,
                            color: 'orange',
                            icon: 'clock',
                        };
                    }
                    return null;
                default:
                    return null;
            }
        },
        getItemClasses(deadline) {
            return {
                'quickpay-deadlines__item--selectable': this.selectable,
                'quickpay-deadlines__item--disabled': this.selectable && deadline.step === 'paid',
                'quickpay-deadlines__item--selected':
                    this.selectable && this.selectedDeadlineId === deadline.id,
            };
        },
        getDateClasses(deadline) {
            return {
                'quickpay-deadlines__item-date--paid': deadline.step === 'paid',
            };
        },
        getAmountClasses(deadline) {
            return {
                'quickpay-deadlines__item-amount--paid': deadline.step === 'paid',
            };
        },
        handleDeadlineClick(deadline) {
            if (!this.selectable || deadline.step === 'paid') {
                return;
            }
            this.selectedDeadlineId = deadline.id;
        },
        handlePayClick() {
            if (this.selectedDeadline) {
                this.$emit('pay-deadline', this.selectedDeadline);
            }
        },
        handlePayWholeDocument() {
            this.$emit('pay-whole-document');
        },
    },
};
</script>

<style lang="scss" scoped>
.quickpay-deadlines {
    width: 100%;
    min-height: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-2);

    &--selectable {
        .quickpay-deadlines__preview,
        .quickpay-deadlines__list {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-1);
        }
    }
    &__header {
        display: flex;
        align-items: center;
    }
    &__title {
        color: var(--colors-common-text-standard-default);
    }
    &__preview {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }
    &__wrapper {
        display: flex;
        flex-direction: column;

        @media (min-width: 1024px) {
            overflow: hidden;
        }
    }
    &__toggle {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--spacing-0);
        padding: var(--spacing-2);
        background: none;
        border: none;
        cursor: pointer;
        align-self: center;
        &-text {
            color: var(--colors-common-text-subtle-default);
        }
        &-icon {
            color: var(--colors-common-text-subtle-default);
        }
        &:hover {
            .quickpay-deadlines__toggle-text {
                text-decoration: underline;
            }
        }
    }
    &__list {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: var(--spacing-3) 0;
        background: var(--colors-common-background-default);
        border-radius: var(--spacing-2);
        z-index: 10;
        overflow-y: auto;
        &:empty {
            display: none;
        }

        @media (min-width: 1024px) {
            padding: var(--spacing-1);
        }
    }
    &__item {
        height: 44px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--spacing-2) var(--spacing-3);
        transition: all 0.2s ease;
        &--selectable {
            background: var(--colors-common-background-default);
            border-radius: var(--spacing-1);
            box-shadow: 0px 0px 9px 0px rgba(143, 143, 143, 0.17);
            cursor: pointer;
            &:hover {
                box-shadow: 2px 0px 10px 3px rgba(0, 0, 0, 0.12);
            }
        }
        &--selected {
            box-shadow: 2px 0px 10px 3px rgba(0, 0, 0, 0.12);
        }
        &--disabled {
            cursor: not-allowed;
            &:hover {
                box-shadow: 0px 0px 9px 0px rgba(143, 143, 143, 0.17);
            }
        }
        &-radio {
            flex-shrink: 0;
        }
        &-main {
            display: flex;
            align-items: center;
            gap: var(--spacing-2);
        }
        &-info {
            display: flex;
            align-items: center;
            gap: var(--spacing-2);
        }
        &-date {
            color: var(--colors-common-text-subtle-default);
            &--paid {
                color: var(--colors-common-text-standard-disabled-light);
            }
        }
        &-tag {
            flex-shrink: 0;
        }
        &-amount {
            color: var(--colors-common-text-standard-caption);
            text-align: end;
            .quickpay-deadlines--selectable & {
                color: var(--colors-common-text-standard-default);
            }
            &--paid {
                color: var(--colors-common-text-standard-disabled-light);
            }
        }
    }
    &__confirmation {
        position: sticky;
        bottom: var(--spacing-1);
        left: 0;
        right: 0;
        background: var(--colors-common-background-darkest-default);
        border-radius: var(--spacing-1);
        z-index: 100;
        padding: var(--spacing-2) var(--spacing-4);
        &::before {
            content: '';
            position: absolute;
            left: 0;
            right: 0;
            bottom: 100%;
            height: 100%;
            background: linear-gradient(180deg, rgba(255, 255, 255, 0) 42.62%, #ffffff 100%);
            pointer-events: none;
            z-index: -1;
        }
        @media (min-width: 1024px) {
            width: 100%;
            max-width: 400px;
            bottom: 32px;
            left: auto;
            right: auto;
            margin-top: 0;
            margin-bottom: 0;
            transform: none;
        }
        &--with-footer {
            @media (min-width: 1024px) {
                bottom: 100px;
            }
        }
        &-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: var(--spacing-3);
            max-width: 400px;
            margin: 0 auto;

            @media (min-width: 1024px) {
                max-width: none;
            }
        }
        &-info {
            display: flex;
            flex-direction: column;
        }
        &-amount {
            color: var(--colors-common-text-contrast-default);
        }

        &-date {
            color: var(--colors-common-text-contrast-default);
        }
    }
    &__divider {
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--colors-common-text-standard-caption);

        &::before,
        &::after {
            content: '';
            flex: 1 1 0;
            height: 1px;
            background: var(--colors-common-text-standard-caption);
        }
        &::before {
            margin-right: var(--spacing-3);
        }
        &::after {
            margin-left: var(--spacing-3);
        }
    }
    &__pay-whole {
        display: flex;
        justify-content: center;
    }
}

.confirmation-block-enter-active {
    transition:
        transform 300ms ease-out 10ms,
        opacity 300ms ease-out 10ms;
}
.confirmation-block-leave-active {
    transition:
        transform 300ms ease-out,
        opacity 300ms ease-out;
}
.confirmation-block-enter-from {
    opacity: 0;
    transform: translateY(100px);
}
.confirmation-block-leave-to {
    opacity: 0;
    transform: translateY(100px);
}
.deadline-item-enter-active {
    transition:
        opacity 300ms ease-out,
        transform 300ms ease-out;
    transition-delay: calc(var(--item-index) * 10ms);
}
.deadline-item-leave-active {
    transition:
        opacity 300ms ease-in,
        transform 300ms ease-in;
    transition-delay: calc((var(--total-items) - var(--item-index) - 1) * 10ms);
}
.deadline-item-enter-from {
    opacity: 0;
    transform: translateY(-8px);
}
.deadline-item-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}
.deadline-item-move {
    transition: transform 250ms ease-out;
}
</style>
